import styled, { css } from 'styled-components'
import {
  financeControlSkin,
  FONT_STACK,
  sureco,
} from '../shared/finance.styles'

/* Teal tint for selected and in-progress states; sureco has no light teal. */
const TEAL_TINT = '#E6F6F7'

/* ---------- Page layout ---------- */

/* Same type, colour and control skin as the billing profile pages. */
export const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${sureco.fg1};
  font-family: ${FONT_STACK};

  ${financeControlSkin}
`

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${sureco.fg1};
  margin: 0;
`

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

/* Neutral bordered button, as the billing profile page's Export. */
const neutralButton = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${sureco.border};
  background: ${sureco.white};
  color: ${sureco.fg1};
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover:not(:disabled) {
    background: ${sureco.offWhite};
    border-color: ${sureco.fg3};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${sureco.focusRing};
  }

  &:disabled {
    background: ${sureco.offWhite};
    border-color: ${sureco.borderSoft};
    color: ${sureco.fg3};
    cursor: not-allowed;
  }
`

export const ResyncButton = styled.button`
  ${neutralButton}
`

/** Groups the right-hand actions in a SectionHeader (which is space-between). */
export const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

/* Period-scoped downloads, neutral like the billing profile Export. */
export const DownloadPeriodButton = styled.button`
  ${neutralButton}
`

/* ---------- Banners ---------- */

const banner = (tone: 'error' | 'notice') => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid
    ${tone === 'error' ? sureco.errorBorder : sureco.noticeBorder};
  background: ${tone === 'error' ? sureco.errorLight : sureco.noticeLight};
  color: ${tone === 'error' ? sureco.errorText : sureco.noticeText};
`

export const UnmatchedBanner = styled.div`
  ${banner('error')}
`

export const UnmatchedBannerCopy = styled.div`
  flex: 1;
  min-width: 0;

  .anticon {
    color: ${sureco.danger};
  }
`

/*
 * Banner internal layout:
 *   [icon] Title text        ← icon vertically centered with the title row
 *          Description text  ← hangs under the title (not under the icon)
 */
export const BannerTextStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const BannerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > .anticon {
    font-size: 16px;
    line-height: 1;
    flex-shrink: 0;
  }
`

export const BannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
`

/* 24px = icon (16px) + gap (8px) — description lines up under the title. */
export const BannerDescription = styled.div`
  padding-left: 24px;
  font-size: 13px;
  line-height: 1.45;
`

const bannerActions = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

export const UnmatchedBannerActions = styled.div`
  ${bannerActions}
`

const bannerPrimaryButton = (fill: string, hover: string) => css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${fill};
  background: ${fill};
  color: ${sureco.white};
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: ${hover};
    border-color: ${hover};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const bannerDismissButton = (color: string) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${color};
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: rgba(17, 42, 67, 0.06);
  }
`

export const UnmatchedBannerPrimaryButton = styled.button`
  ${bannerPrimaryButton(sureco.danger, sureco.dangerDark)}
`

export const UnmatchedBannerDismissButton = styled.button`
  ${bannerDismissButton(sureco.errorText)}
`

/* ---------- Parse-warning banner (amber, fixable inline) ---------- */

export const WarningBanner = styled.div`
  ${banner('notice')}
`

export const WarningBannerCopy = styled.div`
  flex: 1;
  min-width: 0;

  .anticon {
    color: ${sureco.noticeText};
  }
`

export const WarningBannerActions = styled.div`
  ${bannerActions}
`

export const WarningBannerPrimaryButton = styled.button`
  ${bannerPrimaryButton(sureco.noticeText, '#7A3509')}
`

export const WarningBannerDismissButton = styled.button`
  ${bannerDismissButton(sureco.noticeText)}
`

/* ---------- Stats row ---------- */

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export const StatCard = styled.div`
  background: ${sureco.white};
  border: 1px solid ${sureco.border};
  border-radius: 12px;
  box-shadow: ${sureco.shadowSm};
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const StatLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${sureco.fg3};
  margin-bottom: 6px;
`

export const StatValue = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: ${sureco.fg1};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

export const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${sureco.offWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${sureco.fg3};
`

/* ---------- Shared: status + version badges ---------- */

/* Same families as the billing profile pills: amber waits, green is done. */
const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> =
  {
    draft: {
      bg: sureco.noticeLight,
      text: sureco.noticeText,
      dot: '#F59E0B',
    },
    sending: { bg: TEAL_TINT, text: sureco.tealDark, dot: sureco.teal },
    delivered: {
      bg: sureco.successLight,
      text: sureco.successText,
      dot: '#22C55E',
    },
    failed: { bg: sureco.errorLight, text: sureco.errorText, dot: '#EF4444' },
    // Not a backend status — a display-only variant of `failed` for deliveries
    // that reached some recipients. Warning orange rather than failure red, so
    // a delivery where most recipients succeeded doesn't read as a total loss.
    partially_delivered: { bg: '#FFEDD5', text: '#9A3412', dot: '#F97316' },
  }

export const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 999px;
  background: ${({ $status }) => STATUS_COLORS[$status]?.bg ?? sureco.offWhite};
  color: ${({ $status }) => STATUS_COLORS[$status]?.text ?? sureco.fg3};
  white-space: nowrap;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $status }) =>
      STATUS_COLORS[$status]?.dot ?? sureco.placeholder};
  }
`

export const VersionBadge = styled.span<{ $isHistorical?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $isHistorical }) => ($isHistorical ? sureco.fg3 : sureco.teal)};
  background: ${sureco.white};
  border: 1px solid
    ${({ $isHistorical }) => ($isHistorical ? sureco.border : sureco.teal)};
  border-radius: 4px;
  padding: 1px 6px;
`

/* ---------- Shared: table cell components ---------- */

export const CompanyCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${sureco.fg1};
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;

  > div {
    min-width: 0;
    max-width: 100%;

    > span:first-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
      max-width: 100%;
    }
  }
`

const tag = (bg: string, border: string, color: string) => css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${color};
  background: ${bg};
  border: 1px solid ${border};
  border-radius: 4px;
  padding: 2px 8px;
`

export const UnmatchedTag = styled.span`
  ${tag(sureco.errorLight, sureco.errorBorder, sureco.errorText)}
`

// Amber: fixable inline (vs UnmatchedTag's blocking-red).
export const IssuesTag = styled.span`
  ${tag(sureco.noticeLight, sureco.noticeBorder, sureco.noticeText)}
  cursor: help;
`

export const FileNameCell = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${sureco.fg2};
  min-width: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${({ $clickable }) =>
    $clickable
      ? css`
          &:hover > span {
            color: ${sureco.teal};
            text-decoration: underline;
          }
        `
      : ''}
`

/* ---------- Shared: action buttons ---------- */

export const ActionButtonsRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: nowrap;
`

export const IconButton = styled.button<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid ${sureco.border};
  background: ${sureco.white};
  color: ${({ $color }) => $color ?? sureco.fg3};
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: ${sureco.offWhite};
    border-color: ${({ $color }) => $color ?? sureco.fg3};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

/* Compact row action; tone picks the accent, as on the billing pages. */
const rowAction = (color: string, border: string, hoverBg: string) => css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  border: 1px solid ${border};
  background: ${sureco.white};
  color: ${color};

  &:hover:not(:disabled) {
    border-color: ${color};
    background: ${hoverBg};
  }

  &:disabled {
    border-color: ${sureco.borderSoft};
    background: ${sureco.offWhite};
    color: ${sureco.fg3};
    cursor: not-allowed;
  }
`

export const SendButton = styled.button`
  ${rowAction(sureco.teal, sureco.teal, TEAL_TINT)}
`

export const DeleteButton = styled.button`
  ${rowAction(sureco.danger, sureco.errorBorder, sureco.errorLight)}
`

export const ArchiveButton = styled.button<{ $archived?: boolean }>`
  ${({ $archived }) =>
    $archived
      ? rowAction(sureco.noticeText, sureco.noticeBorder, sureco.noticeLight)
      : rowAction(sureco.fg2, sureco.border, sureco.offWhite)}
`

export const ArchivedTag = styled.span`
  ${tag(sureco.offWhite, sureco.border, sureco.fg3)}
`

export const DownloadLink = styled.button`
  ${rowAction(sureco.fg2, sureco.border, sureco.offWhite)}
`

/* ---------- Unused legacy history panel styles (kept for backward compat) ---------- */

export const HistoryPanel = styled.div`
  overflow: hidden;
  min-width: 0;
  padding: 2px 0;
`

export const HistoryTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 20px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 4px;
    bottom: 4px;
    width: 2px;
    background: ${sureco.border};
    border-radius: 1px;
  }
`

export const HistoryEntry = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 6px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: -14px;
    top: 50%;
    width: 10px;
    height: 2px;
    background: ${sureco.border};
    transform: translateY(-50%);
  }

  &:hover {
    background: ${sureco.offWhite};
  }
`

export const HistoryEntryFile = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${sureco.fg2};

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const HistoryEntryMeta = styled.span`
  font-size: 11px;
  color: ${sureco.fg3};
  white-space: nowrap;
  flex-shrink: 0;
`

export const HistoryEntryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`

export const HistoryRow = styled.div<{ $isLatest?: boolean }>`
  display: grid;
  grid-template-columns: 54px 120px 1fr auto auto auto;
  gap: 12px;
  align-items: center;
  padding: 8px 4px;
  border-bottom: 1px solid ${sureco.border};
  font-size: 12px;

  &:last-child {
    border-bottom: none;
  }

  background: ${({ $isLatest }) => ($isLatest ? sureco.white : 'transparent')};
  border-radius: ${({ $isLatest }) => ($isLatest ? '6px' : '0')};
  padding-left: ${({ $isLatest }) => ($isLatest ? '8px' : '4px')};
  padding-right: ${({ $isLatest }) => ($isLatest ? '8px' : '4px')};
`
